import { type ReactNode } from 'react'
import { cn } from '#app/utils/misc.tsx'

// The house page frame — one set of margins for every page reachable from the
// Writing list. The values are not new: they are exactly what the four
// interactive demos (`meteora`, `delta-neutral`, `p2e`, `general-ledger`) already
// use, so adopting them here is what makes an essay, an architecture page and a
// demo app share a silhouette.
//
// Desktop caps at max-w-4xl (896px), narrowed from max-w-7xl on 2026-08-05 at
// Jacob's call: one width for every /writing/* page. The 7xl frame left prose
// blocks and demo panels at visibly different widths on the same page, and no
// arrangement of the two read as deliberate. Issue 32's constraint was a ceiling,
// not a target, so tightening it does not violate it.
const FRAME = 'font-poppins mx-auto w-full max-w-4xl px-4 py-16 sm:px-6 lg:px-8'

export function PageFrame({
	as: Tag = 'main',
	children,
	className,
}: {
	as?: 'main' | 'div'
	children: ReactNode
	className?: string
}) {
	return <Tag className={cn(FRAME, className)}>{children}</Tag>
}

// The reading column now MATCHES the frame (max-w-4xl) rather than sitting at
// 672px inside it — the point of the 2026-08-05 narrowing is that /writing/*
// has one width. Kept as a named wrapper so a distinct measure can be restored
// in one place if 896px (~95 characters) reads too wide for long-form prose.
export function ReadingColumn({
	children,
	className,
}: {
	children: ReactNode
	className?: string
}) {
	return (
		<div className={cn('mx-auto w-full max-w-4xl', className)}>{children}</div>
	)
}

// Deliberately no <FullBleed> escape hatch. The one candidate — the harness
// diagram — is authored at a 672px viewBox so it renders 1:1 in the reading
// column; widening it would upscale its labels, not reveal detail. Add one when
// something genuinely needs the width (a wide table, an embedded demo).
